/* eslint-disable no-console */
import { useCallback, useRef, useMemo, useState, useEffect } from 'react';

import { metrics } from 'metrics';

import { ChoiceOption, ContextStore } from '../types';

interface MetricsApi {
  track: (event: string, properties?: Record<string, unknown>) => void;
}

interface Metrics {
  api: MetricsApi;
}

export interface SessionContext {
  impactaiSession: string;
  presetId: string | null;
  presetSession: string | null;
  timestamp: number;
  source: string;
  version: string;
}

// Simple UUID generation - no dependencies required
const generateUUID = (): string => {
  // First try crypto.randomUUID (modern browsers)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    try {
      return crypto.randomUUID();
    } catch {
      // Fall through to next method
    }
  }

  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    try {
      const template = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
      return template.replace(/[xy]/g, (c) => {
        const array = new Uint8Array(1);
        crypto.getRandomValues(array);
        const r = array[0] % 16;
        // eslint-disable-next-line no-bitwise
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    } catch {
      // Fall through to final fallback
    }
  }

  return `fallback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

interface TrackingService {
  track: (event: string, properties?: Record<string, unknown>) => void;
  isAvailable: boolean;
}

export interface ImpactAiTracker {
  // Feature-level events
  trackDrawer: (isOpen: boolean) => void;

  // Preset-level events
  trackPresetSelect: (preset: { id: string; title: string }) => void;
  trackPresetComplete: (preset: { id: string; title: string }, success: boolean) => void;
  trackBackToPresets: () => void;
  trackGoBack: (targetActionIndex: number) => void;

  // Conversation flow events
  trackChoiceSelect: (choice: ChoiceOption, actionIndex: number) => void;
  trackMultipleChoiceSubmit: (
    selectedChoices: Array<{ key: string; choice: ChoiceOption }>,
    actionIndex: number
  ) => void;
  trackTemplateSubmit: (template: string, actionIndex: number) => void;
  trackMessageSent: (messageLength: number, actionIndex: number) => void;
  trackMethodExecute: (
    methodName: string,
    payload: unknown,
    success: boolean,
    duration: number,
    actionIndex: number
  ) => void;
  trackActionComplete: (actionType: string, actionIndex: number, duration: number) => void;

  // Context tracking
  trackContextUpdate: (context: ContextStore) => void;

  // Error and recovery tracking
  trackError: (
    errorType: string,
    errorMessage: string,
    actionIndex: number,
    recoverable: boolean
  ) => void;
  trackErrorRecovery: (originalError: string, recoveryAction: string) => void;

  // Form integration events
  trackFormFieldUpdate: (
    form: string,
    field: string,
    value: unknown,
    source: 'ai' | 'user'
  ) => void;
  trackFormNavigation: (url: string, section?: string) => void;
  trackFormSubmission: (form: string, success: boolean) => void;

  // Session management
  startPresetSession: (presetId: string) => void;
  endPresetSession: () => void;
  getSessionContext: () => SessionContext;
}

const createTrackingService = (): TrackingService => {
  // Check if metrics API is available and functional
  const isMetricsAvailable = (): boolean => {
    try {
      const typedMetrics = metrics as Metrics;
      return !!(window?.analytics && typeof typedMetrics?.api?.track === 'function');
    } catch {
      return false;
    }
  };

  const track = (event: string, properties = {}) => {
    try {
      if (isMetricsAvailable()) {
        (metrics as Metrics).api.track(event, properties);
      } else if (process.env.NODE_ENV !== 'production') {
        // Fallback: console.log in development, silent in production
        console.log(`[ANALYTICS BLOCKED] ${event}:`, properties);
      }
    } catch (error) {
      // Silent failure - never break user experience
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Analytics tracking failed:', error);
      }
    }
  };

  return {
    track,
    isAvailable: isMetricsAvailable(),
  };
};

export const useImpactAiTracking = (): ImpactAiTracker => {
  const trackingService = useMemo(() => createTrackingService(), []);

  // Simple session management - no persistence, no timing
  const [impactAiSessionId] = useState(() => generateUUID());
  const presetSessionIdRef = useRef<string | null>(null);
  const currentPresetRef = useRef<string | null>(null);
  const drawerOpenTimeRef = useRef<number | null>(null);
  const drawerIsOpenRef = useRef<boolean>(false);

  // Context update debouncing
  const contextUpdateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestContextRef = useRef<ContextStore | null>(null);

  // Clean up pending context updates on unmount
  useEffect(
    () => () => {
      if (contextUpdateTimeoutRef.current) {
        clearTimeout(contextUpdateTimeoutRef.current);
        contextUpdateTimeoutRef.current = null;
      }
    },
    []
  );

  const getSessionContext = useCallback(
    () => ({
      impactaiSession: impactAiSessionId,
      presetId: currentPresetRef.current,
      presetSession: presetSessionIdRef.current,
      timestamp: Date.now(),
      source: 'impact_ai_drawer',
      version: '1.0',
    }),
    [impactAiSessionId]
  );

  const trackEvent = useCallback(
    (event: string, additionalProperties: Record<string, unknown> = {}) => {
      const sessionContext = getSessionContext();
      const properties = {
        event,
        ...sessionContext,
        ...additionalProperties,
      };
      if (process.env.NODE_ENV !== 'production') {
        console.log(`[ANALYTICS] ${event}:`, properties);
      }
      trackingService.track('impactai', properties);
    },
    [getSessionContext, trackingService]
  );

  // Feature-level events
  const trackDrawer = useCallback(
    (isOpen: boolean) => {
      const wasOpen = drawerIsOpenRef.current;

      if (isOpen && !wasOpen) {
        // Drawer is opening
        drawerOpenTimeRef.current = Date.now();
        drawerIsOpenRef.current = true;
        trackEvent('drawer_open');
      } else if (!isOpen && wasOpen) {
        // Drawer is closing
        const duration = drawerOpenTimeRef.current ? Date.now() - drawerOpenTimeRef.current : 0;
        drawerIsOpenRef.current = false;
        drawerOpenTimeRef.current = null;
        trackEvent('drawer_close', { duration });
      }
    },
    [trackEvent]
  );

  // Preset-level events
  const trackPresetSelect = useCallback(
    (preset: { id: string; title: string }) => {
      trackEvent('preset_select', {
        preset_title: preset.title,
      });
    },
    [trackEvent]
  );

  const trackPresetComplete = useCallback(
    (preset: { id: string; title: string }, success: boolean) => {
      presetSessionIdRef.current = null;
      trackEvent('preset_complete', {
        preset_title: preset.title,
        success,
      });
    },
    [trackEvent]
  );

  const trackBackToPresets = useCallback(() => {
    presetSessionIdRef.current = null;
    trackEvent('back_to_presets');
  }, [trackEvent]);

  const trackGoBack = useCallback(
    (targetActionIndex: number) => {
      trackEvent('go_back', {
        target_action_index: targetActionIndex,
      });
    },
    [trackEvent]
  );

  // Conversation flow events
  const trackChoiceSelect = useCallback(
    (choice: ChoiceOption, actionIndex: number) => {
      trackEvent('choice_select', {
        choice_value: choice.value,
        choice_text: choice.text,
        action_index: actionIndex,
      });
    },
    [trackEvent]
  );

  const trackMultipleChoiceSubmit = useCallback(
    (selectedChoices: Array<{ key: string; choice: ChoiceOption }>, actionIndex: number) => {
      trackEvent('multiple_choice_submit', {
        selected_choices: selectedChoices,
        action_index: actionIndex,
      });
    },
    [trackEvent]
  );

  const trackTemplateSubmit = useCallback(
    (template: string, actionIndex: number) => {
      trackEvent('template_submit', {
        template,
        action_index: actionIndex,
      });
    },
    [trackEvent]
  );

  const trackMessageSent = useCallback(
    (messageLength: number, actionIndex: number) => {
      trackEvent('message_sent', {
        message_length: messageLength,
        action_index: actionIndex,
      });
    },
    [trackEvent]
  );

  const trackMethodExecute = useCallback(
    (
      methodName: string,
      payload: unknown,
      success: boolean,
      duration: number,
      actionIndex: number
    ) => {
      trackEvent('method_execute', {
        method_name: methodName,
        success,
        duration,
        action_index: actionIndex,
        payload: JSON.stringify(payload),
      });
    },
    [trackEvent]
  );

  const trackActionComplete = useCallback(
    (actionType: string, actionIndex: number, duration: number) => {
      trackEvent('action_complete', {
        action_type: actionType,
        action_index: actionIndex,
        duration,
      });
    },
    [trackEvent]
  );

  // Context tracking with debouncing
  const trackContextUpdate = useCallback(
    (context: ContextStore) => {
      // Store the latest context
      latestContextRef.current = context;

      // Clear any existing timeout
      if (contextUpdateTimeoutRef.current) {
        clearTimeout(contextUpdateTimeoutRef.current);
      }

      // Set a new timeout to send the update after 500ms
      contextUpdateTimeoutRef.current = setTimeout(() => {
        if (latestContextRef.current) {
          trackEvent('context_update', {
            context: latestContextRef.current,
          });
          latestContextRef.current = null;
        }
        contextUpdateTimeoutRef.current = null;
      }, 500);
    },
    [trackEvent]
  );

  // Error and recovery tracking
  const trackError = useCallback(
    (errorType: string, errorMessage: string, actionIndex: number, recoverable: boolean) => {
      trackEvent('error', {
        error_type: errorType,
        error_message: errorMessage.substring(0, 200), // Limit error message length
        action_index: actionIndex,
        recoverable,
        preset_id: currentPresetRef.current,
      });
    },
    [trackEvent]
  );

  const trackErrorRecovery = useCallback(
    (originalError: string, recoveryAction: string) => {
      trackEvent('error_recovery', {
        original_error: originalError.substring(0, 200),
        recovery_action: recoveryAction,
        preset_id: currentPresetRef.current,
      });
    },
    [trackEvent]
  );

  // Form integration events
  const trackFormFieldUpdate = useCallback(
    (form: string, field: string, value: unknown, source: 'ai' | 'user') => {
      trackEvent('form_field_update', {
        form,
        field,
        source,
        value_type: typeof value,
        value_length: typeof value === 'string' ? value.length : undefined,
        preset_id: currentPresetRef.current,
      });
    },
    [trackEvent]
  );

  const trackFormNavigation = useCallback(
    (url: string, section?: string) => {
      trackEvent('form_navigation', {
        url,
        section,
        preset_id: currentPresetRef.current,
      });
    },
    [trackEvent]
  );

  const trackFormSubmission = useCallback(
    (form: string, success: boolean) => {
      trackEvent('form_submission', {
        form,
        success,
        preset_id: currentPresetRef.current,
      });
    },
    [trackEvent]
  );

  // Session management
  const startPresetSession = useCallback((presetId: string) => {
    const newPresetSessionId = generateUUID();
    presetSessionIdRef.current = newPresetSessionId;
    currentPresetRef.current = presetId;
  }, []);

  const endPresetSession = useCallback(() => {
    presetSessionIdRef.current = null;
    currentPresetRef.current = null;
  }, []);

  return {
    trackDrawer,
    trackPresetSelect,
    trackPresetComplete,
    trackBackToPresets,
    trackGoBack,
    trackChoiceSelect,
    trackMultipleChoiceSubmit,
    trackTemplateSubmit,
    trackMessageSent,
    trackMethodExecute,
    trackActionComplete,
    trackContextUpdate,
    trackError,
    trackErrorRecovery,
    trackFormFieldUpdate,
    trackFormNavigation,
    trackFormSubmission,
    startPresetSession,
    endPresetSession,
    getSessionContext,
  };
};
